import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

const KEY = "stock-items";

export interface StockItem {
	id: string;
	code: string;
	name: string;
	description: string | null;
	category: string | null;
	uom: string | null;
	isActive: boolean;
	isInventoryItem: boolean;
	isSalesItem: boolean;
	isPurchaseItem: boolean;
	manageSerialNumbers: boolean;
	manageBatchNumbers: boolean;
	currentStock: string | null;
	reservedAmount: string | null;
	onOrderAmount: string | null;
	maxStock: string | null;
	minStock: string | null;
	reorderPoint: string | null;
	sapItemCode: string | null;
	sapItemGroup: number | null;
	sapSalesUnit: string | null;
	sapPurchaseUnit: string | null;
	lastSyncedAt: string | null;
	syncStatus: string | null;
	createdAt: string;
	updatedAt: string;
	totalStock?: string | null;
}

export interface StockItemWarehouse {
	id: string;
	warehouseId: string;
	warehouseCode: string | null;
	warehouseName: string | null;
	currentStock: string | null;
	reservedAmount: string | null;
	onOrderAmount: string | null;
	minStock: string | null;
	maxStock: string | null;
}

export function useStockItemList(options?: {
	search?: string | undefined;
	createdAtFrom?: string | undefined;
	createdAtTo?: string | undefined;
	status?: string | undefined;
	page?: number;
	limit?: number;
}) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	return useQuery({
		queryKey: [KEY, "list", options],
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			if (options?.search) params.set("search", options.search);
			if (options?.createdAtFrom) params.set("createdAtFrom", options.createdAtFrom);
			if (options?.createdAtTo) params.set("createdAtTo", options.createdAtTo);
			if (options?.status) params.set("status", options.status);
			return api.get<{ items: StockItem[]; total: number }>(
				`/api/stock-items?${params.toString()}`,
			);
		},
	});
}

export function useStockItemDetail(id: string) {
	return useQuery({
		queryKey: [KEY, "detail", id],
		queryFn: () =>
			api.get<StockItem & { warehouseStocks: StockItemWarehouse[] }>(`/api/stock-items/${id}`),
		enabled: !!id,
	});
}

export function useStockItemCategories() {
	return useQuery({
		queryKey: [KEY, "categories"],
		queryFn: () =>
			api.get<{ categories: { category: string; code: number | null }[] }>(
				"/api/stock-items/categories",
			),
		staleTime: 5 * 60 * 1000,
	});
}

export function useStockItemUnits() {
	return useQuery({
		queryKey: [KEY, "units"],
		queryFn: () => api.get<{ units: string[] }>("/api/stock-items/units"),
		staleTime: 5 * 60 * 1000,
	});
}

export interface StockMovement {
	date: string;
	warehouseCode: string;
	document: string;
	counterpartName: string | null;
	quantity: number;
	price: number | null;
	currency: string | null;
}

export function useStockItemMovements(
	id: string | null,
	options?: { startDate?: string; endDate?: string },
) {
	return useQuery({
		queryKey: [KEY, "movements", id, options],
		queryFn: () => {
			const params = new URLSearchParams();
			if (options?.startDate) params.set("startDate", options.startDate);
			if (options?.endDate) params.set("endDate", options.endDate);
			return api.get<{ movements: StockMovement[] }>(
				`/api/stock-items/${id}/movements?${params.toString()}`,
			);
		},
		enabled: !!id,
	});
}

export interface SerialWarehouse {
	itemCode: string;
	warehouseCode: string;
	serialNo: string;
	quantity: number;
}

export function useStockItemSerials(id: string | null) {
	return useQuery({
		queryKey: [KEY, "serials", id],
		queryFn: () => api.get<{ serials: SerialWarehouse[] }>(`/api/stock-items/${id}/serials`),
		enabled: !!id,
	});
}
